// Copyright (C) Lembani Sakala - All rights reserved.
// Lembani Sakala.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../../../utils/localStorage';

interface Stats {
  total: number;
  pending: number;
  resolved: number;
  inProgress: number;
}

interface StatCard {
  id: string;
  title: string;
  value: number;
  icon: string;
  color: string;
  gradient: string;
  change?: string;
  trend?: 'up' | 'down';
}

export const DashboardOverview = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/enquiries/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading dashboard...</p>
      </div>
    );
  }

  const statCards: StatCard[] = [
    {
      id: 'total',
      title: 'Total Enquiries',
      value: stats?.total || 0,
      icon: '📊',
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      change: '+12%',
      trend: 'up',
    },
    {
      id: 'pending',
      title: 'Pending',
      value: stats?.pending || 0,
      icon: '⏳',
      color: '#f6ad55',
      gradient: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
      change: '+5',
      trend: 'up',
    },
    {
      id: 'inProgress',
      title: 'In Progress',
      value: stats?.inProgress || 0,
      icon: '⚡',
      color: '#4299e1',
      gradient: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
      change: '3',
      trend: 'up',
    },
    {
      id: 'resolved',
      title: 'Resolved',
      value: stats?.resolved || 0,
      icon: '✅',
      color: '#48bb78',
      gradient: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
      change: '+18',
      trend: 'up',
    },
  ];

  const quickActions = [
    {
      label: 'View Pending Enquiries',
      icon: '💬',
      color: '#f6ad55',
      action: () => navigate('/admin/enquiries'),
    },
    {
      label: 'View All Bookings',
      icon: '📅',
      color: '#4299e1',
      action: () => navigate('/admin/bookings'),
    },
    {
      label: 'Manage Settings',
      icon: '⚙️',
      color: '#9f7aea',
      action: () => {},
    },
  ];

  const recentActivity = [
    { action: 'New enquiry received', user: 'Getrude Mutemwa', time: '2 min ago', icon: '📩', color: '#667eea' },
    { action: 'Enquiry resolved', user: 'Nalolo Chilala', time: '15 min ago', icon: '✅', color: '#48bb78' },
    { action: 'Booking confirmed', user: 'Linda Phiri', time: '1 hour ago', icon: '📅', color: '#4299e1' },
    { action: 'Enquiry marked in progress', user: 'Chibwe Chama', time: '2 hours ago', icon: '⚡', color: '#f6ad55' },
  ];

  const completionRate = stats ? Math.round((stats.resolved / stats.total) * 100) : 0;
  const pendingRate = stats ? Math.round((stats.pending / stats.total) * 100) : 0;
  const inProgressRate = stats ? Math.round((stats.inProgress / stats.total) * 100) : 0;

  return (
    <div style={styles.container}>
      {/* Welcome Section */}
      <div style={styles.welcomeSection}>
        <div>
          <h2 style={styles.welcomeTitle}>Welcome back! 👋</h2>
          <p style={styles.welcomeSubtitle}>Here's what's happening with your enquiries today.</p>
        </div>
        <button style={styles.refreshButton} onClick={fetchStats}>
          🔄 Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        {statCards.map((card, index) => (
          <div
            key={card.id}
            style={{
              ...styles.statCard,
              animationDelay: `${index * 100}ms`,
            }}
          >
            <div style={styles.statCardHeader}>
              <div style={{ ...styles.statIcon, background: card.gradient }}>
                {card.icon}
              </div>
              {card.change && (
                <div style={styles.changeIndicator}>
                  <span style={{ color: card.trend === 'up' ? '#48bb78' : '#f56565' }}>
                    {card.trend === 'up' ? '↑' : '↓'} {card.change}
                  </span>
                </div>
              )}
            </div>
            <div style={styles.statCardBody}>
              <h3 style={styles.statValue}>{card.value}</h3>
              <p style={styles.statTitle}>{card.title}</p>
            </div>
            <div style={{ ...styles.statCardFooter, background: card.gradient }} />
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div style={styles.contentGrid}>
        {/* Progress Overview */}
        <div style={styles.progressCard}>
          <div style={styles.cardHeader}>
            <h4 style={styles.cardTitle}>Enquiry Status Overview</h4>
            <span style={styles.badge}>Live</span>
          </div>
          <div style={styles.progressSection}>
            <div style={styles.progressItem}>
              <div style={styles.progressLabel}>
                <span style={styles.progressDot} />
                <span style={styles.progressText}>Resolved</span>
              </div>
              <span style={styles.progressPercent}>{completionRate}%</span>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${completionRate}%`, background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)' }} />
              </div>
            </div>
            <div style={styles.progressItem}>
              <div style={styles.progressLabel}>
                <span style={{ ...styles.progressDot, background: '#f6ad55' }} />
                <span style={styles.progressText}>Pending</span>
              </div>
              <span style={styles.progressPercent}>{pendingRate}%</span>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${pendingRate}%`, background: 'linear-gradient(90deg, #f6ad55 0%, #ed8936 100%)' }} />
              </div>
            </div>
            <div style={styles.progressItem}>
              <div style={styles.progressLabel}>
                <span style={{ ...styles.progressDot, background: '#4299e1' }} />
                <span style={styles.progressText}>In Progress</span>
              </div>
              <span style={styles.progressPercent}>{inProgressRate}%</span>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${inProgressRate}%`, background: 'linear-gradient(90deg, #4299e1 0%, #3182ce 100%)' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div style={styles.activityCard}>
          <div style={styles.cardHeader}>
            <h4 style={styles.cardTitle}>Recent Activity</h4>
            <button style={styles.viewAllButton}>View All →</button>
          </div>
          <div style={styles.activityList}>
            {recentActivity.map((activity, index) => (
              <div key={index} style={styles.activityItem}>
                <div style={{ ...styles.activityIcon, background: activity.color }}>
                  {activity.icon}
                </div>
                <div style={styles.activityContent}>
                  <p style={styles.activityAction}>{activity.action}</p>
                  <p style={styles.activityUser}>{activity.user} • {activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={styles.quickActionsCard}>
        <h4 style={styles.cardTitle}>Quick Actions</h4>
        <div style={styles.quickActionsGrid}>
          {quickActions.map((action, index) => (
            <button
              key={index}
              style={styles.actionButton}
              onClick={action.action}
            >
              <div style={{ ...styles.actionIcon, background: action.color }}>
                {action.icon}
              </div>
              <span style={styles.actionLabel}>{action.label}</span>
              <span style={styles.actionArrow}>→</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    animation: 'fadeIn 0.5s ease-in-out',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e9ecef',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#6c757d',
    fontSize: '16px',
    fontWeight: '500',
  },
  welcomeSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  welcomeTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
    marginBottom: '8px',
  },
  welcomeSubtitle: {
    fontSize: '16px',
    color: '#6c757d',
    margin: 0,
  },
  refreshButton: {
    padding: '12px 24px',
    background: '#fff',
    border: '2px solid #e9ecef',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '24px',
    marginBottom: '32px',
  },
  statCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    animation: 'slideUp 0.5s ease-out',
  },
  statCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  statIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  changeIndicator: {
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 8px',
    background: '#f8f9fa',
    borderRadius: '8px',
  },
  statCardBody: {
    marginBottom: '12px',
  },
  statValue: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
    marginBottom: '4px',
  },
  statTitle: {
    fontSize: '14px',
    color: '#6c757d',
    margin: 0,
    fontWeight: '500',
  },
  statCardFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '4px',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    marginBottom: '32px',
  },
  progressCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  activityCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
  },
  badge: {
    padding: '4px 12px',
    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  progressSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  progressLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  progressDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#48bb78',
  },
  progressText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
    flex: 1,
  },
  progressPercent: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6c757d',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    background: '#e9ecef',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '8px',
    transition: 'width 1s ease-out',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  activityItem: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    background: '#f8f9fa',
    borderRadius: '12px',
    transition: 'all 0.2s',
  },
  activityIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: 0,
  },
  activityContent: {
    flex: 1,
    minWidth: 0,
  },
  activityAction: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a202c',
    margin: 0,
    marginBottom: '4px',
  },
  activityUser: {
    fontSize: '12px',
    color: '#6c757d',
    margin: 0,
  },
  viewAllButton: {
    background: 'transparent',
    border: 'none',
    color: '#667eea',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  quickActionsCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '24px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    background: '#f8f9fa',
    border: '2px solid transparent',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left',
  },
  actionIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: '#fff',
  },
  actionLabel: {
    flex: 1,
  },
  actionArrow: {
    fontSize: '18px',
    opacity: 0.5,
  },
};
